"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import type {
  CRealizationsArticle,
  CRealizationsContent,
  CRealizationsItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import { fetchCatalogPageContent } from "@/lib/cms/catalogContent";
import { useCatalogLocation } from "@/lib/cms/useCatalogLocation";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import {
  cRealizationsTranslations,
  type CRealizationsTranslations,
} from "./CRealizations.i18n";
import styles from "./CRealizations.module.css";

/**
 * Formatuje wartosc pola "sector" - splituje po przecinku i/lub enterach,
 * trimuje elementy, filtruje puste i laczy separatorem middle-dot (`·`).
 */
function formatSectorList(raw: string): string {
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" · ");
}

/**
 * CRealizations - visitor view komponentu `c_dedicated_realizations` (Portfolio).
 *
 * **Data source:** `C_DEDICATED_REALIZATIONS_CONTROLLER::getApiContent()` dispatcher po
 * `?article_id=N` -> article view, else list view. Payload w
 * `lib/api/cms-catalog.ts::CRealizationsContent`.
 *
 * **Layout wg makiety Agendo:**
 *   list:    hero header (HTML z config[header_html]) + tabs kategorii
 *            (client-side filter po `item.type`) + 3-kolumnowy grid kart
 *            (zdjęcie tła + badge kategorii + logo klienta w overlay + tytuł +
 *            opis + producent + "Zobacz ->" link).
 *   article: back link + tytuł/subtitle + banner + rozbudowany opis w
 *            sekcjach (o marce, cele, korzyści, opinia klienta) + galeria +
 *            info o kliencie.
 *
 * **Security:** wszystkie pola HTML (header_html, short_text, text,
 * about_brand, goals, benefits, client_about_realization) pochodzą z admin
 * panelu Imperial (TinyMCE) - trusted source, `dangerouslySetInnerHTML` OK.
 */
export function CRealizations({ content }: CRealizationsProps) {
  const t = useT(cRealizationsTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {content.view === "article" && content.article ? (
        <ArticleView
          article={content.article}
          listHref={content.list_href ?? null}
          t={t}
        />
      ) : (
        <ListView content={content} t={t} />
      )}
    </div>
  );
}

export type CRealizationsProps = {
  content: CRealizationsContent;
};

/* ─── List view ─────────────────────────────────────────────────────────── */

function ListView({
  content,
  t,
}: {
  content: CRealizationsContent;
  t: CRealizationsTranslations;
}) {
  const loc = useCatalogLocation();

  /* SSR-y content = "widok bazowy" (bez filtra). Stan lokalny nadpisuje go po
     zmianie kategorii - re-fetch przez /api/cms/catalog-content z id_category. */
  const initialItems: CRealizationsItem[] = Array.isArray(content.items)
    ? content.items
    : [];
  const initialPagination = content.pagination;

  /* activeCategoryId: null = "Wszystkie" (bez filtra), int = konkretna kategoria
     ze słownika `realizations_categories`. Backend echo'uje `active_category_id`
     dla SSR (jeśli URL miał ?id_category=N). */
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(
    content.active_category_id ?? null,
  );

  /* Displayed content: SSR init dla stanu bazowego, potem fetch przy zmianie. */
  const [displayedItems, setDisplayedItems] = useState<CRealizationsItem[]>(initialItems);
  const [displayedPagination, setDisplayedPagination] = useState(initialPagination);
  const [switching, setSwitching] = useState(false);

  /* Kategorie do tabs ze słownika Imperial (backend zwraca zawsze pełną listę
     `content.categories`). NIE filtrujemy per bieżąca strona (tak było wcześniej -
     w kategorii "puste w pierwszej stronie" tab znikał). */
  const categories = useMemo(() => {
    return Array.isArray(content.categories) ? content.categories : [];
  }, [content.categories]);

  /* Fetch przy zmianie kategorii - server-side filter po id_category. */
  const initialCategoryRef = useRef(content.active_category_id ?? null);
  useEffect(() => {
    if (activeCategoryId === initialCategoryRef.current) {
      /* Reset do stanu SSR bez fetcha. */
      setDisplayedItems(initialItems);
      setDisplayedPagination(initialPagination);
      return;
    }
    let cancelled = false;
    setSwitching(true);
    fetchCatalogPageContent(loc.slug, loc.locale, {
      page: 1,
      idCategory: activeCategoryId,
    })
      .then((c) => {
        if (cancelled) return;
        const next = (c as CRealizationsContent | null) ?? null;
        setDisplayedItems(Array.isArray(next?.items) ? next.items : []);
        setDisplayedPagination(next?.pagination);
      })
      .finally(() => {
        if (!cancelled) setSwitching(false);
      });
    return () => {
      cancelled = true;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- initialItems/initialPagination
       to referencje z SSR, celowo pomijamy żeby re-fetch szedł tylko przy zmianie kategorii. */
  }, [activeCategoryId, loc.slug, loc.locale]);

  const loadPage = async (nextPage: number): Promise<CRealizationsItem[]> => {
    const c = await fetchCatalogPageContent(loc.slug, loc.locale, {
      page: nextPage,
      idCategory: activeCategoryId,
    });
    const next = (c as CRealizationsContent | null) ?? null;
    return Array.isArray(next?.items) ? next.items : [];
  };

  return (
    <>
      {content.header_html && content.header_html.trim() !== "" && (
        <section
          className={styles.hero}
          dangerouslySetInnerHTML={{ __html: content.header_html }}
        />
      )}

      {categories.length > 0 && (
        <nav className={styles.tabs} aria-label={t.categoriesAria}>
          <button
            type="button"
            className={
              activeCategoryId === null
                ? `${styles.tabPill} ${styles.tabPillActive}`
                : styles.tabPill
            }
            onClick={() => setActiveCategoryId(null)}
            aria-pressed={activeCategoryId === null}
            disabled={switching}
          >
            {t.allCategories}
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={
                activeCategoryId === cat.id
                  ? `${styles.tabPill} ${styles.tabPillActive}`
                  : styles.tabPill
              }
              onClick={() => setActiveCategoryId(cat.id)}
              aria-pressed={activeCategoryId === cat.id}
              disabled={switching}
            >
              {cat.name}
            </button>
          ))}
        </nav>
      )}

      {displayedItems.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : (
        <CatalogLoadMore<CRealizationsItem>
          initialItems={displayedItems}
          initialPage={displayedPagination?.page ?? 1}
          totalPages={displayedPagination?.total_pages ?? 1}
          totalCount={displayedPagination?.total_count}
          getKey={(item) => item.id}
          loadPage={loadPage}
          /* URL nie odzwierciedla filtra client-only - wyłączamy pushState żeby
             nie linkować `/pageN` który po refresh dałby inne wyniki (bez filtra). */
          updateUrl={activeCategoryId === null}
        >
          {(allItems) => (
            <ul className={styles.grid}>
              {allItems.map((item) => (
                <RealizationCard key={item.id} item={item} t={t} />
              ))}
            </ul>
          )}
        </CatalogLoadMore>
      )}
    </>
  );
}

function RealizationCard({
  item,
  t,
}: {
  item: CRealizationsItem;
  t: CRealizationsTranslations;
}) {
  const href = extractInternalHref(item.href);
  //Badge w lewym górnym rogu karty = freeform pole `label` z admin panelu
  //(np. "System + aplikacja mobilna"). Fallback do `type` (legacy field) gdy label pusty.
  //Wieloetykietowy label (rozdzielony przecinkami/enterami) laczony middle-dot separatorem.
  const badge = formatSectorList((item.label ?? "").trim() || (item.type ?? "").trim());

  const cardInner = (
    <>
      <div className={styles.cardMedia}>
        {item.picture ? (
          <img
            src={item.picture.thumb_src ?? item.picture.full_src}
            alt={item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            className={styles.cardImg}
            loading="lazy"
          />
        ) : (
          <div className={styles.cardImgPlaceholder} aria-hidden="true" />
        )}

        {badge !== "" && <span className={styles.cardBadge}>{badge}</span>}

        {/* Logo overlay: preferuj logo file (item.logo), fallback do nazwy klienta jako tekst
            gdy admin nie wgrał logo. Priorytet nazwy: freeform `client_display_name`
            (pole "Nazwa klienta do publikacji") -> `client.company_name` -> `client.name`. */}
        {item.logo ? (
          <div className={styles.cardLogo}>
            <img
              src={item.logo.full_src}
              alt={item.client_display_name?.trim() || item.client?.company_name || item.name}
              loading="lazy"
            />
          </div>
        ) : (
          (() => {
            const fallbackName = (
              (item.client_display_name ?? "").trim() ||
              item.client?.company_name ||
              item.client?.name ||
              ""
            ).trim();
            if (fallbackName === "") return null;
            return (
              <div className={`${styles.cardLogo} ${styles.cardLogoText}`}>
                <span>{fallbackName}</span>
              </div>
            );
          })()
        )}
      </div>

      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle}>{item.name}</h3>
        {item.short_text && item.short_text.trim() !== "" && (
          <div
            className={styles.cardShort}
            dangerouslySetInnerHTML={{ __html: item.short_text }}
          />
        )}
        <div className={styles.cardFooter}>
          {item.sector && item.sector.trim() !== "" ? (
            <span className={styles.cardSector}>{formatSectorList(item.sector)}</span>
          ) : (
            <span aria-hidden="true" />
          )}
          <span className={styles.cardMore}>{t.readMore}</span>
        </div>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </Link>
      ) : (
        <a
          href={item.href}
          className={styles.cardLink}
          aria-label={item.name}
        >
          {cardInner}
        </a>
      )}
    </li>
  );
}

/* ─── Article view ─────────────────────────────────────────────────────── */

function ArticleView({
  article,
  listHref,
  t,
}: {
  article: CRealizationsArticle;
  listHref: string | null;
  t: CRealizationsTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;

  const heroImage = article.banner ?? article.list_image;
  const heroBadge = formatSectorList((article.label ?? "").trim() || (article.type ?? "").trim());

  //Full-bleed hero + sidebar offset — obie potrzebują JS bo `calc(50% - 50vw)` nie
  //radzi sobie w 2-col layout (article NIE jest wycentrowany w viewport, jest
  //w wąskim mainColumn po lewej stronie od sidebara).
  //  1) Wysokość hero -> `--catalog-sidebar-offset` (sidebar startuje pod hero).
  //  2) article.left/right w viewport -> asymetryczne marginesy hero, tak żeby
  //     rozciągnął się od viewport.left do viewport.right niezależnie od pozycji
  //     rodzica.
  const articleRef = useRef<HTMLElement | null>(null);
  const heroRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const hero = heroRef.current;
    const article = articleRef.current;
    if (!hero || !article) return;
    const root = document.documentElement;
    const update = () => {
      // reset marginów żeby zmierzyć rzeczywistą pozycję HERO (nie article,
      // bo article ma padding — hero.left = article.left + article.padding-left)
      hero.style.marginLeft = "0px";
      hero.style.marginRight = "0px";
      const heroRect = hero.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      hero.style.marginLeft = `-${heroRect.left}px`;
      hero.style.marginRight = `-${vw - heroRect.right}px`;
      // Sidebar wyrównany z GÓRĄ TREŚCI (nie z bottom hero) — dodać margin-bottom hero.
      const heroStyle = window.getComputedStyle(hero);
      const marginBottom = parseFloat(heroStyle.marginBottom) || 0;
      const offset = hero.getBoundingClientRect().height + marginBottom;
      root.style.setProperty("--catalog-sidebar-offset", `${Math.round(offset)}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(article);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty("--catalog-sidebar-offset");
    };
  }, []);

  return (
    <article ref={articleRef} className={styles.article}>
      <header
        ref={heroRef}
        className={`${styles.articleHero} ${heroImage ? "" : styles.articleHeroNoImage}`}
        style={
          heroImage
            ? {
                backgroundImage: `url(${heroImage.full_src})`,
              }
            : undefined
        }
      >
        <div className={styles.articleHeroOverlay} aria-hidden="true" />

        <div className={styles.articleHeroInner}>
          {(backHref !== null || listHref || heroBadge !== "") && (
            <div className={styles.articleHeroBack}>
              {backHref !== null ? (
                <Link href={backHref} className={styles.articleHeroBackLink}>
                  {t.backToList}
                </Link>
              ) : listHref ? (
                <a href={listHref} className={styles.articleHeroBackLink}>
                  {t.backToList}
                </a>
              ) : null}
              {heroBadge !== "" && (
                <span className={styles.articleHeroBadge}>{heroBadge}</span>
              )}
            </div>
          )}

          <div className={styles.articleHeroContent}>
            {article.logo && (
              <div className={styles.articleHeroLogo}>
                <img
                  src={article.logo.full_src}
                  alt={article.client?.company_name ?? article.name}
                />
              </div>
            )}

            <h1 className={styles.articleHeroTitle}>{article.name}</h1>

            {(() => {
              //Preferuj freeform "Nazwa klienta do publikacji" (client_display_name),
              //fallback do company_name z kartoteki (imp_user) gdy freeform pusta.
              const clientName = (
                (article.client_display_name ?? "").trim() ||
                article.client?.company_name ||
                article.client?.name ||
                ""
              );
              const sectorName = formatSectorList((article.sector ?? "").trim());
              if (!clientName && !sectorName) return null;
              return (
                <p className={styles.articleHeroMeta}>
                  {clientName && (
                    <span className={styles.articleHeroClient}>{clientName}</span>
                  )}
                  {clientName && sectorName && (
                    <span className={styles.articleHeroMetaSep} aria-hidden="true">
                      •
                    </span>
                  )}
                  {sectorName && (
                    <span className={styles.articleHeroSector}>{sectorName}</span>
                  )}
                </p>
              );
            })()}
          </div>
        </div>
      </header>

      {article.intro_text && article.intro_text.trim() !== "" && (
        <p className={styles.articleIntro}>{article.intro_text}</p>
      )}

      {Array.isArray(article.boxes) && article.boxes.length > 0 && (
        <ul className={styles.articleBoxes}>
          {article.boxes.map((box, idx) => (
            <li key={idx} className={styles.articleBox}>
              {box.value && (
                <span className={styles.articleBoxValue}>{box.value}</span>
              )}
              {box.label && (
                <span className={styles.articleBoxLabel}>{box.label}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <ArticleSection
        title={t.aboutBrand}
        html={article.about_brand}
      />
      <ArticleSection title={t.goals} html={article.goals} />
      <ArticleSection title={t.benefits} html={article.benefits} variant="benefits" />
      <ArticleSection title={t.description} html={article.text}>
        {article.online_url && article.online_label && (
          <a
            href={article.online_url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.articleOnlineBtn}
          >
            <span>{article.online_label}</span>
            <svg
              className={styles.articleOnlineBtnIcon}
              viewBox="0 0 24 24"
              width="16"
              height="16"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 17L17 7" />
              <path d="M8 7h9v9" />
            </svg>
          </a>
        )}
      </ArticleSection>
      <ArticleSection
        title={t.implementationProcess}
        html={article.implementation_process}
        variant="steps"
      />
      <ArticleSection
        title={t.solutions}
        html={article.text_solutions}
        variant="tags"
      />
      <ArticleSection
        title={t.clientAboutRealization}
        html={article.client_about_realization}
        variant="quote"
        author={article.client_about_realization_author}
        authorRole={article.client_about_realization_role}
      />

      {article.gallery && article.gallery.length > 0 && (
        <section className={styles.articleGallery}>
          <h3 className={styles.articleSectionTitle}>{t.gallery}</h3>
          <ul className={styles.galleryGrid}>
            {article.gallery.map((img) => (
              <li key={img.id} className={styles.galleryItem}>
                <a
                  href={img.full_src}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={img.description || img.name}
                >
                  <img
                    src={img.thumb_src ?? img.full_src}
                    alt={img.description || img.name}
                    width={img.width ?? undefined}
                    height={img.height ?? undefined}
                    loading="lazy"
                  />
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

    </article>
  );
}

/**
 * Sekcja treści artykułu - h3 + HTML z TinyMCE (trusted source).
 * Nie renderuje nic gdy `html` puste - unikamy pustych sekcji z samym nagłówkiem.
 */
function ArticleSection({
  title,
  html,
  variant,
  author,
  authorRole,
  children,
}: {
  title: string;
  html: string | null | undefined;
  variant?: "quote" | "benefits" | "steps" | "tags";
  /** Autor cytatu (variant="quote") - nazwa bold w footer'ze. */
  author?: string | null;
  /** Rola/funkcja autora - regular po przecinku obok nazwy. */
  authorRole?: string | null;
  /** Dodatkowa zawartość pod body (np. przycisk CTA "Zobacz online"). */
  children?: ReactNode;
}) {
  if (!html || html.trim() === "") return null;

  //Variant "tags" - plain textarea z admin, entery = separator elementow zakresu prac.
  //Rendering przez ul>li (pill per linia) zamiast dangerouslySetInnerHTML.
  if (variant === "tags") {
    const items = html
      .split(/\r?\n/)
      .map((s) => s.replace(/<[^>]*>/g, "").trim())
      .filter((s) => s !== "");
    if (items.length === 0) return null;
    return (
      <section className={styles.articleSectionTags}>
        <h3 className={styles.articleSectionTitle}>{title}</h3>
        <ul className={styles.articleTags}>
          {items.map((item, idx) => (
            <li key={idx} className={styles.articleTag}>
              {item}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  const sectionClass =
    variant === "benefits"
      ? styles.articleSectionBenefits
      : variant === "steps"
        ? styles.articleSectionSteps
        : styles.articleSection;
  const bodyClass =
    variant === "quote"
      ? styles.articleQuote
      : variant === "benefits"
        ? styles.articleBenefits
        : variant === "steps"
          ? styles.articleSteps
          : styles.articleSectionBody;
  const authorName = (author ?? "").trim();
  const authorRoleText = (authorRole ?? "").trim();
  const showAuthor = variant === "quote" && (authorName !== "" || authorRoleText !== "");

  return (
    <section className={sectionClass}>
      <h3 className={styles.articleSectionTitle}>{title}</h3>
      <div className={bodyClass} dangerouslySetInnerHTML={{ __html: html }} />
      {children}
      {showAuthor ? (
        <footer className={styles.articleQuoteFooter}>
          {authorName !== "" ? (
            <span className={styles.articleQuoteAuthor}>{authorName}</span>
          ) : null}
          {authorName !== "" && authorRoleText !== "" ? (
            <span className={styles.articleQuoteAuthorSep}>, </span>
          ) : null}
          {authorRoleText !== "" ? (
            <span className={styles.articleQuoteAuthorRole}>{authorRoleText}</span>
          ) : null}
        </footer>
      ) : null}
    </section>
  );
}
